import type { Dayjs } from "dayjs";
import moment from "moment-timezone";
import { ENUM_USER_ROLE } from "../../../enums/common";
import {
  evaluateSlotSelectionCriteria,
  getEndDateTimeForSlotRange,
} from "./slotCriteria";
import type { TimeSlotMeta } from "./utils";
import { buildSlotSelectionPayload } from "./utils";

const AMERICA_NEW_YORK = "America/New_York";
const DAY_KEY_FORMAT = "YYYY-MM-DD";

export type SlotValidationDisplay = {
  etBody: string;
  etTimezoneAbbr: string;
  etTimeRange: string;
  etDayOffset: number;
  nyValidation: {
    isWeekend: boolean;
    isShortNotice: boolean;
    isLateNight: boolean;
    timezoneAbbr: string;
  };
  convertedBlock: {
    timezoneLabel: string;
    timezoneAbbr: string;
    body: string;
  } | null;
  convertedTimeRange: string | null;
  convertedDayOffset: number | null;
  oppositeDaytimeOk: boolean | null;
  oppositeAbbr: string;
};

export function getSlotValidationDisplayForIndex(
  selectedDate: Dayjs,
  slots: TimeSlotMeta[],
  slotIndex: number,
  oppositeUserTimezone: string | null,
  userRole: string
): SlotValidationDisplay | null {
  const payload = buildSlotSelectionPayload(
    selectedDate,
    slots,
    slotIndex,
    null
  );

  const criteria = evaluateSlotSelectionCriteria(
    selectedDate,
    slots,
    slotIndex,
    null,
    oppositeUserTimezone
  );

  const rawDate = payload.date;
  const rawStart = payload.startTime;
  const rawEnd = payload.endTime;

  const selectedStartDateTime = moment(rawDate)
    .hour(moment(rawStart).hour())
    .minute(moment(rawStart).minute())
    .second(0)
    .millisecond(0);

  const etStartDateTime = selectedStartDateTime.clone().tz(AMERICA_NEW_YORK);
  const etTimezoneAbbr = etStartDateTime.format("z");
  const baseEtDay = moment
    .tz(selectedDate.format(DAY_KEY_FORMAT), AMERICA_NEW_YORK)
    .startOf("day");
  const etDayOffset = etStartDateTime
    .clone()
    .startOf("day")
    .diff(baseEtDay, "days");
  const etEndDateTime = getEndDateTimeForSlotRange(
    rawDate,
    rawStart,
    rawEnd
  ).tz(AMERICA_NEW_YORK);

  const nyValidation = {
    isWeekend: criteria.isWeekend,
    isShortNotice: criteria.isShortNotice,
    isLateNight: criteria.isLateNight,
    timezoneAbbr: etTimezoneAbbr,
  };

  let convertedBlock: SlotValidationDisplay["convertedBlock"] = null;
  let convertedTimeRange: SlotValidationDisplay["convertedTimeRange"] = null;
  let convertedDayOffset: SlotValidationDisplay["convertedDayOffset"] = null;

  if (oppositeUserTimezone) {
    const convertedStartDateTime = selectedStartDateTime
      .clone()
      .tz(oppositeUserTimezone);
    const timezoneAbbr = convertedStartDateTime.format("z");
    const timezoneLabel =
      userRole === ENUM_USER_ROLE.AMBASSADOR ? "Vendor" : "Ambassador";
    const baseOppDay = moment
      .tz(selectedDate.format(DAY_KEY_FORMAT), oppositeUserTimezone)
      .startOf("day");
    convertedDayOffset = convertedStartDateTime
      .clone()
      .startOf("day")
      .diff(baseOppDay, "days");
    const selectedEndDateTime = getEndDateTimeForSlotRange(
      rawDate,
      rawStart,
      rawEnd
    );
    const convertedEndDateTime = selectedEndDateTime
      .clone()
      .tz(oppositeUserTimezone);

    const startDateStr = convertedStartDateTime.format("dddd, MMM D, YYYY");
    const endDateStr = convertedEndDateTime.format("dddd, MMM D, YYYY");
    const startTimeStr = convertedStartDateTime.format("hh:mm A");
    const endTimeStr = convertedEndDateTime.format("hh:mm A");
    convertedTimeRange = `${startTimeStr} - ${endTimeStr}`;
    const body =
      startDateStr === endDateStr
        ? `${startDateStr} at ${startTimeStr} - ${endTimeStr}`
        : `${startDateStr} at ${startTimeStr} - ${endDateStr} at ${endTimeStr}`;

    convertedBlock = {
      timezoneLabel,
      timezoneAbbr,
      body,
    };
  }

  const startDateStrEt = etStartDateTime.format("dddd, MMM D, YYYY");
  const endDateStrEt = etEndDateTime.format("dddd, MMM D, YYYY");
  const startTimeStrEt = etStartDateTime.format("hh:mm A");
  const endTimeStrEt = etEndDateTime.format("hh:mm A");
  const etTimeRange = `${startTimeStrEt} - ${endTimeStrEt}`;
  const etBody =
    startDateStrEt === endDateStrEt
      ? `${startDateStrEt} at ${startTimeStrEt} - ${endTimeStrEt}`
      : `${startDateStrEt} at ${startTimeStrEt} - ${endDateStrEt} at ${endTimeStrEt}`;

  return {
    etBody,
    etTimezoneAbbr,
    etTimeRange,
    etDayOffset,
    nyValidation,
    convertedBlock,
    convertedTimeRange,
    convertedDayOffset,
    oppositeDaytimeOk: criteria.oppositeDaytimeOk,
    oppositeAbbr: oppositeUserTimezone
      ? selectedStartDateTime.clone().tz(oppositeUserTimezone).format("z")
      : "",
  };
}
